import type { OrgId } from '#shared/types/ids'
import type { Org } from '#shared/types/Org'
import type {
    OrgTransaction,
    OrgTransactionEntityTalentBaseUserShow,
    OrgTransactionFull,
    OrgTransactionPaymentResponse,
    OrgTransactionStatus
} from '#shared/types/OrgTransaction'
import type { GetTalentBaseUserProfile, TalentBaseJobMatchesResponse } from '#shared/types/TalentBaseRequest'
import type { TalentBaseUserProfile, UserProfile } from '#shared/types/User'

// Define the SearchOptions interface
interface SearchOptions {
    page?: number
    hitsPerPage?: number
    filters?: string
    ruleContexts?: string[]
    attributesToRetrieve?: string[]
    [key: string]: any
}

/**
 * Composable for TalentBase functionality
 */
export function useTalentBase() {
    const { $api } = useNuxtApp()
    const { authFetch } = useAuthFetch()

    /**
     * Load user's TalentBase requests
     */
    const loadUserTalentBaseRequests = async (): Promise<OrgTransactionFull[]> => {
        return (await $api.get('/talentbase/requests')).data
    }

    /**
     * Order TalentBase credits
     */
    const orderTalentBaseCredits = async (orgId: OrgId, amount: number, redirectUrl: string): Promise<string> => {
        return (await $api.post(`/talentbase/${orgId}/order`, { amount, redirectUrl })).data
    }

    /**
     * Load TalentBase request
     */
    const loadTalentBaseRequest = async (id: OrgTransaction['id']): Promise<OrgTransactionFull> => {
        return (await $api.get(`/talentbase/requests/${id}`)).data
    }

    /**
     * Get TalentBase user profile
     */
    const getTalentBaseUserProfile = async (slug: TalentBaseUserProfile['slug'], org?: Org['id'] | Org['slug']) => {
        return (await $api.get(`/talentbase/profiles/${slug}`, { params: { org } })).data
    }

    /**
     * Create TalentBase user request
     */
    const createTalentBaseUserRequest = async (
        orgId: OrgId,
        slug: TalentBaseUserProfile['slug'],
        data: Omit<OrgTransactionEntityTalentBaseUserShow, 'id' | 'type' | 'status'>
    ) => {
        return (await $api.post(`/talentbase/${orgId}/profiles/${slug}`, data)).data
    }

    /**
     * Accept TalentBase request
     */
    const acceptTalentBaseRequest = async (id: OrgTransaction['id']): Promise<OrgTransaction> => {
        return (await $api.post(`/talentbase/requests/${id}/accept`)).data
    }

    /**
     * Reject TalentBase request
     */
    const rejectTalentBaseRequest = async (id: OrgTransaction['id'], message?: string): Promise<OrgTransaction> => {
        return (await $api.post(`/talentbase/requests/${id}/reject`, { message })).data
    }

    /**
     * Load TalentBase facets (server-side: the public Typesense key cannot read
     * talentbase_members, whose documents hold the candidates' real names)
     */
    const loadTalentBaseFacets = async (): Promise<Record<string, Record<string, number>>> => {
        return (await $api.get('/talentbase/facets')).data
    }

    /**
     * Get profiles by status
     */
    const getProfilesByStatus = async (
        orgSlug: Org['slug'],
        status?: OrgTransactionStatus,
        limit?: number
    ): Promise<{ profiles: UserProfile[]; total: number }> => {
        return (
            await $api.get(`/talentbase/${orgSlug}/profiles`, {
                params: { status, limit }
            })
        ).data
    }

    /**
     * Get TalentBase user profiles
     *
     * @param query - searched string
     * @param group - filtering by OrgTransactionStatus
     * @param options - filtered options based on talent base filter
     * @param org - id of the organization
     * @returns List of talent base profiles with pagination info
     */
    const getTalentBaseUserProfiles = async (
        query: string = '',
        group: OrgTransactionStatus | undefined,
        options: Partial<SearchOptions> = {},
        org: Org['id']
    ): Promise<any> => {
        return (await $api.post('/talentbase/profiles', { query, options, org, group })).data
    }

    /**
     * TalentBase candidates closest to a job posting (org members only).
     * Nuxt server API, not the Functions API: GET /api/talentbase/matches/:jobId
     */
    const getJobTalentMatches = async (jobId: string): Promise<TalentBaseJobMatchesResponse> => {
        return await authFetch<TalentBaseJobMatchesResponse>(`/api/talentbase/matches/${jobId}`)
    }

    return {
        getJobTalentMatches,
        loadUserTalentBaseRequests,
        orderTalentBaseCredits,
        loadTalentBaseRequest,
        getTalentBaseUserProfile,
        createTalentBaseUserRequest,
        acceptTalentBaseRequest,
        rejectTalentBaseRequest,
        loadTalentBaseFacets,
        getProfilesByStatus,
        getTalentBaseUserProfiles
    }
}
